import { useCallback, useRef, useState } from 'react'
import { NodeSelection } from 'prosemirror-state'
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { t } from 'i18next';

interface ImageUploadResponse {
  id: string;
  presignedUrl: string;
  originalFilename: string;
}
interface UseImageUploadOptions {
  maxSizeInMB?: number
  allowedTypes?: string[]
  uploadFunction?: (file: File) => Promise<ImageUploadResponse>
}

const defaultUploadImage = async (file: File, quizId: string, questionId: string = null): Promise<ImageUploadResponse> => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    let url = `${process.env.REACT_APP_API_URL}/question-image/upload?quizId=${quizId}`
    if (questionId) {
      url = url + `&questionId${questionId}`
    }

    const res = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    return {
      id: res.data.imageId,
      presignedUrl: res.data.url,
      originalFilename: file.name
    }
  } catch (e) {
    console.log("🚀 ~ defaultUploadImage ~ e:", e)
    throw new Error(t('error_messages.image_upload_failed'))
  }
}

export const useImageUpload = (
  editor: any,
  options: UseImageUploadOptions = {}
) => {
  const {
    maxSizeInMB = 5,
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    uploadFunction = defaultUploadImage
  } = options

  const { quizId, questionId = null } = useParams()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isUploading, setIsUploading] = useState(false)

  const validateFile = useCallback((file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return t('error_messages.image_type_unsupported')
    }

    if (file.size > maxSizeInMB * 1024 * 1024) {
      return t('error_messages.image_too_large', { size: maxSizeInMB })
    }

    return null
  }, [allowedTypes, maxSizeInMB, t])

  const handleImageUpload = useCallback(() => {
    if (isUploading) return
    fileInputRef.current?.click()
  }, [isUploading])


  const onImageSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !editor) return

    const validationError = validateFile(file)
    if (validationError) {
      event.target.value = ''
      throw new Error(validationError)
    }

    setIsUploading(true)
    try {
      const uploadResponse = await uploadFunction(file, quizId, questionId)
      editor.chain().focus().setImage({
        src: uploadResponse.presignedUrl,
        'data-image-id': uploadResponse.id,
        'data-original-filename': uploadResponse.originalFilename,
        alt: uploadResponse.originalFilename
      }).run()
    } catch (error) {
      console.error('Error uploading image:', error)
      throw new Error(t('error_messages.image_upload_failed'))
    } finally {
      setIsUploading(false)
      event.target.value = ''
    }
  }, [editor, validateFile, uploadFunction])

  const isImageSelected = useCallback(() => {
    if (!editor) return false
    return editor.state.selection instanceof NodeSelection &&
      editor.state.selection.node?.type.name === 'image'
  }, [editor])

  const selectedImageHasExplanation = useCallback(() => {
    if (!editor || !isImageSelected()) return false

    const { selection } = editor.state
    return selection instanceof NodeSelection &&
      selection.node?.attrs['data-explanation']
  }, [editor, isImageSelected])

  const getSelectedImageExplanationIndex = useCallback(() => {
    if (!editor || !isImageSelected()) return null

    const { selection } = editor.state
    if (!(selection instanceof NodeSelection)) return null

    const explanationIndex = selection.node?.attrs['data-explanation']
    return explanationIndex ? Number(explanationIndex) : null
  }, [editor, isImageSelected])


  const getSelectedImageAttrs = useCallback(() => {
    if (!editor || !isImageSelected()) return null

    const { selection } = editor.state
    if (selection instanceof NodeSelection) {
      return selection.node.attrs
    }
    return null
  }, [editor, isImageSelected])

  const updateSelectedImage = useCallback((attrs: Record<string, any>) => {
    if (!editor || !isImageSelected()) return false

    editor.chain().focus().updateAttributes('image', attrs).run()
    return true
  }, [editor, isImageSelected])

  return {
    fileInputRef,
    handleImageUpload,
    onImageSelect,
    isImageSelected: isImageSelected(),
    selectedImageHasExplanation: selectedImageHasExplanation(),
    getSelectedImageExplanationIndex,
    getSelectedImageAttrs,
    updateSelectedImage,
    validateFile,
    isUploading
  }
}
